import PropTypes from 'prop-types';
import { useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { CheckboxInput } from '@hivebrite/design-system/Inputs';
import { Link, FakeLink } from '@hivebrite/design-system/Link';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import SuccessCircleCheck from 'hb-react/shared/icons/SuccessCircleCheck';
import copyToClipboard from 'hb-react/shared/utils/copyToClipboard';

import Card, { StyledCard } from '../MembershipCard';

const PublicCard = ({ onLogin, ...membership }) => {
  const { brandBarColor } = membership;

  return (
    <StyledCard brandBarColor={brandBarColor}>
      <Card {...membership}>
        <Box alignItems="flex-start" mt={{ _: 5, l: 0 }}>
          <Button
            onClick={() => onLogin(membership)}
            dataTestId="redirect-to-form-button"
          >
            {s__('Memberships|Log in to buy')}
          </Button>
        </Box>
      </Card>
    </StyledCard>
  );
};

const AlreadyOwned = ({ id, ...props }) => {
  const { brandBarColor } = props;

  return (
    <StyledCard brandBarColor={brandBarColor}>
      <Card {...props}>
        <Box alignItems="flex-start" mt={{ _: 5, l: 0 }}>
          <Box mr={2}>
            <SuccessCircleCheck width={20} height={20} />
          </Box>
          <Box flexDirection="column" maxWidth={182}>
            {s__('Memberships|You have already purchased this membership')}
            <Link to={Routes.me_my_memberships_path()} mt={1}>
              {s__('Memberships|Manage your memberships')}
            </Link>
          </Box>
        </Box>
      </Card>
    </StyledCard>
  );
};

const PurchasableCard = ({
  onPurchase,
  buttonText = s__('Memberships|Purchase now'),
  ...membership
}) => {
  const { brandBarColor } = membership;

  return (
    <StyledCard brandBarColor={brandBarColor}>
      <Card {...membership}>
        {onPurchase && (
          <Box flexDirection="column" mr={-1} mt={2} pt={4}>
            {/* TODO: Remove on cart factory ∧ */}
            <Button
              onClick={() => onPurchase(membership)}
              id={`buy-membership-${membership.id}`}
            >
              {buttonText}
            </Button>
            {/* TODO: Remove on cart factory ∨ */}
            <CopyableLink membership={membership} />
          </Box>
        )}
      </Card>
    </StyledCard>
  );
};

const ReadOnlyCard = ({
  buttonText = s__('Memberships|Join Group to purchase'),
  ...membership
}) => {
  const { brandBarColor } = membership;

  return (
    <StyledCard brandBarColor={brandBarColor}>
      <Card {...membership}>
        <Box alignItems="flex-start" mt={{ _: 5, l: 0 }}>
          <Button isDisabled>{buttonText}</Button>
        </Box>
      </Card>
    </StyledCard>
  );
};

const OfflineCard = ({ programme = {}, ...membership }) => {
  const { brandBarColor } = membership;
  const {
    external_link: externalLink,
    instruction_type: instructionType,
    offline_payment_document: offlinePaymentDocument,
  } = programme;

  const href =
    instructionType === 'document' && offlinePaymentDocument
      ? offlinePaymentDocument
      : externalLink;

  return (
    <StyledCard brandBarColor={brandBarColor}>
      <Card {...membership}>
        {href && (
          <Box alignItems="flex-start" mt={{ _: 5, l: 0 }}>
            <Button
              as="a"
              href={href}
              target="_blank"
              rel="noreferrer noopener"
            >
              {s__('Memberships|Purchase now')}
            </Button>
          </Box>
        )}
      </Card>
    </StyledCard>
  );
};

const SelectableCard = ({
  onSelect,
  isSelected = false,
  disabled = false,
  ...membership
}) => {
  const [checked, setChecked] = useState(isSelected);

  const { id, brandBarColor } = membership;

  const onChange = () => {
    setChecked(!checked);

    onSelect(membership);
  };

  return (
    <StyledCard pr={4} brandBarColor={brandBarColor} disabled={disabled}>
      <Box flexDirection="column">
        <Box flexDirection="column" flexGrow={1}>
          <Card {...membership}>
            <Box flexDirection="column" mt={6}>
              {/* TODO: Remove on cart factory ∧ */}
              <CheckboxInput
                label={__('Select membership')}
                onChange={onChange}
                checked={checked}
                id={`membership-${id}`}
                dataTestId={`membership-${id}`}
                disabled={disabled}
                ariaLabel={__('Select membership')}
              />

              {/* TODO: Remove on cart factory ∨ */}
              <CopyableLink membership={membership} />
            </Box>
          </Card>
        </Box>
      </Box>
    </StyledCard>
  );
};

const Membership = ({
  onLogin,
  onPurchase,
  onSelect,
  subscription = {},
  programme = {},
  readOnly = false,
  isPublic = false,
  ...props
}) => {
  if (onSelect && onPurchase) {
    throw new Error(
      'Membership cant be selectable, and purchasable at the same time',
    );
  }

  if (isPublic) {
    return <PublicCard {...props} onLogin={onLogin} />;
  }

  if (subscription.owned_by_user && !subscription.renewable) {
    return <AlreadyOwned {...props} />;
  }

  if (programme?.online_payment === false || !props.is_purchasable) {
    return <OfflineCard {...props} programme={programme} />;
  }

  if (onSelect) {
    if (readOnly) {
      return <ReadOnlyCard {...props} />;
    }

    return <SelectableCard {...props} onSelect={onSelect} />;
  }

  if (onPurchase) {
    if (readOnly) {
      return <ReadOnlyCard {...props} />;
    }

    return (
      <PurchasableCard
        buttonText={
          subscription.renewable
            ? s__('Memberships|Renew membership')
            : s__('Memberships|Purchase now')
        }
        {...props}
        onPurchase={onPurchase}
      />
    );
  }

  return <PublicCard {...props} onLogin={onLogin} />;
};

const CopyableLink = ({ membership }) => {
  const { currentSession } = useDefaultAppContexts();
  const isAdmin = currentSession.networkAdmin;

  return isAdmin ? (
    <FakeLink
      pt={6}
      onClick={() => {
        let link = [];

        if (membership.meta.parent.type === 'Topics::Topic') {
          link = [
            window.location.origin,
            Routes.topic_membership_path(
              membership.meta.parent.id,
              membership.id,
            ),
          ].join('');
        } else {
          link = [
            window.location.origin,
            Routes.membership_path(membership.id),
          ].join('');
        }

        copyToClipboard(link);
      }}
      id={`share-membership-${membership.id}`}
    >
      {s__('Memberships|Copy shareable link')}
    </FakeLink>
  ) : null;
};
/* TODO: Remove on cart factory ∧ */

export default Membership;

Membership.propTypes = {
  id: PropTypes.number.isRequired,
  description: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  currency: PropTypes.shape({
    code: PropTypes.string.isRequired,
    symbol: PropTypes.string.isRequired,
  }).isRequired,
  validity: PropTypes.string,
  periodicity: PropTypes.string.isRequired,
  subscription: PropTypes.shape({
    owned_by_user: PropTypes.bool,
  }),
  _links: PropTypes.shape({
    purchase: PropTypes.string.isRequired,
  }).isRequired,
  buttonText: PropTypes.string,
  onPurchase: PropTypes.func,
  onSelect: PropTypes.func,
  onLogin: PropTypes.func,
  programme: PropTypes.shape({
    online_payment: PropTypes.bool.isRequired,
    external_link: PropTypes.string,
    instruction_type: PropTypes.string,
    offline_payment_document: PropTypes.string,
  }),
  disabled: PropTypes.bool,
};
